import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ModerationNav } from '../components/ModerationNav';
import {
  fetchQueue,
  resolveEvent,
  type MeResponse,
  type ModeratorAction,
  type QueueEvent,
  type QueueResponse,
} from '../lib/api';
import { confirmDialog } from '../lib/dialog';

function label(value: string): string {
  return value.replace(/_/g, ' ');
}

/** Opus 4.7 second-pass review payload stored on moderation_event.detail.opus. */
interface OpusReviewBlock {
  decision: 'dismiss' | 'uphold' | 'warn' | 'suspend' | 'escalate';
  effective_decision: 'dismiss' | 'uphold' | 'warn' | 'suspend' | 'escalate';
  confidence: number;
  reasoning: string;
  category_correction?: string | null;
  reviewed_at?: string;
  model?: string;
}

/** Map Opus's decision string to the queue action class that should
 *  be visually highlighted as the recommended choice. */
function recommendedAction(
  decision: OpusReviewBlock['effective_decision'],
): ModeratorAction | null {
  switch (decision) {
    case 'dismiss':
      return 'dismiss';
    case 'uphold':
    case 'warn':
      // Warn isn't a separate action on the queue — Uphold is the closest
      // 'flag is valid, no account action' verb. Opus treats them as a
      // gradient; the moderator decides.
      return 'uphold';
    case 'suspend':
      return 'suspend_user';
    case 'escalate':
      // Escalate means "I'm not confident enough — a human should decide."
      // No single button to highlight.
      return null;
    default:
      return null;
  }
}

function EventCard({
  event,
  crisisEndpoint,
  busy,
  onAct,
}: {
  event: QueueEvent;
  crisisEndpoint: QueueResponse['crisisEscalation'];
  busy: boolean;
  onAct: (eventId: string, action: ModeratorAction, confirmMessage?: string) => void;
}) {
  const isCrisis = event.category === 'self_harm';
  // Opus's second-pass review lives in detail.opus when present. Used to
  // recommend an action inline so the moderator gets a smart prior.
  const opus = (event.detail as { opus?: OpusReviewBlock } | null)?.opus ?? null;
  const opusAction = opus ? recommendedAction(opus.effective_decision) : null;
  return (
    <div className={`event sev-${event.severity}`}>
      <div className="event-head">
        <span className={`badge sev-${event.severity}`}>{event.severity}</span>
        <strong>{label(event.category)}</strong>
        <span className="muted">· {label(event.source)}</span>
      </div>
      {event.messageBody && <blockquote>{event.messageBody}</blockquote>}

      {/* Subject dossier card — moderator-only identity block so the
          reviewer can act on the queue without first drilling into the
          user detail page. Real name + email + account age + a clay
          repeat-offender pill make a serious action serious. */}
      {event.subjectNickname && (
        <div className="event-subject">
          <span className="event-subject-eyebrow">Subject</span>
          <div className="event-subject-line">
            <strong className="event-subject-name">
              {event.subjectRealName || '(no name on file)'}
            </strong>
            {event.subjectUserStatus && event.subjectUserStatus !== 'active' && (
              <span className={`status-pill status-${event.subjectUserStatus}`}>
                {event.subjectUserStatus}
              </span>
            )}
            {event.subjectPriorActions > 0 && (
              <span
                className={
                  event.subjectPriorActions >= 2
                    ? 'status-pill status-banned'
                    : 'status-pill status-suspended'
                }
                title={`${event.subjectPriorActions} prior moderation action(s)`}
              >
                {event.subjectPriorActions} prior
              </span>
            )}
          </div>
          <p className="event-subject-meta muted">
            {event.subjectEmail && <>{event.subjectEmail} · </>}
            nickname <strong>{event.subjectNickname}</strong>
            {event.subjectAccountAgeDays !== null && (
              <>
                {' · '}account{' '}
                {event.subjectAccountAgeDays === 0
                  ? 'created today'
                  : `${event.subjectAccountAgeDays}d old`}
              </>
            )}
          </p>
        </div>
      )}

      <p className="muted event-footer">
        {event.reporterNickname && <>Reported by {event.reporterNickname} · </>}
        {new Date(event.createdAt).toLocaleString()} ·{' '}
        <Link to={`/moderation/events/${event.id}`}>Full context →</Link>
      </p>

      {isCrisis && crisisEndpoint && (
        <div className="crisis">
          <strong>Route to: {crisisEndpoint.name}</strong>
          <p>{crisisEndpoint.contact}</p>
        </div>
      )}

      {/* Opus 4.7 second-pass recommendation. When present, surfaces the
          smarter model's suggested action + confidence + one-sentence
          reasoning so the moderator gets a smart prior before clicking.
          Opus runs automatically on every flag and writes detail.opus —
          this just makes that visible on the queue itself. */}
      {opus && (
        <div
          className={`opus-rec ${opusAction ? `opus-rec--${opusAction}` : 'opus-rec--escalate'}`}
        >
          <span className="opus-rec-eyebrow">Opus 4.7 recommends</span>
          <div className="opus-rec-line">
            <strong className="opus-rec-action">{label(opus.effective_decision)}</strong>
            <span className="opus-rec-conf">{Math.round(opus.confidence * 100)}% confident</span>
            {opus.decision !== opus.effective_decision && (
              <span
                className="opus-rec-downgrade"
                title="Confidence below the action floor; downgraded to escalate"
              >
                ↳ downgraded from {opus.decision}
              </span>
            )}
          </div>
          <p className="opus-rec-reasoning">{opus.reasoning}</p>
        </div>
      )}

      <div className="event-actions">
        {isCrisis ? (
          <>
            <button
              className="mod-action mod-action--escalate"
              disabled={busy}
              onClick={() => onAct(event.id, 'escalate_crisis')}
            >
              <span className="mod-action-verb">Mark escalated</span>
              <span className="mod-action-hint">
                You contacted the community's crisis endpoint.
              </span>
            </button>
            <button
              className="mod-action mod-action--dismiss"
              disabled={busy}
              onClick={() => onAct(event.id, 'dismiss')}
            >
              <span className="mod-action-verb">Dismiss</span>
              <span className="mod-action-hint">Close the event without acting.</span>
            </button>
          </>
        ) : (
          <>
            <button
              className="mod-action mod-action--dismiss"
              data-recommended={opusAction === 'dismiss' ? 'true' : undefined}
              disabled={busy}
              onClick={() => onAct(event.id, 'dismiss')}
            >
              <span className="mod-action-verb">Dismiss</span>
              <span className="mod-action-hint">
                False positive. Message stays as-is, account untouched.
              </span>
              {opusAction === 'dismiss' && <span className="mod-action-rec">★ Opus pick</span>}
            </button>
            <button
              className="mod-action mod-action--uphold"
              data-recommended={opusAction === 'uphold' ? 'true' : undefined}
              disabled={busy}
              onClick={() => onAct(event.id, 'uphold')}
            >
              <span className="mod-action-verb">Uphold</span>
              <span className="mod-action-hint">
                Flag is valid. Message stays hidden; no account action.
              </span>
              {opusAction === 'uphold' && <span className="mod-action-rec">★ Opus pick</span>}
            </button>
            <button
              className="mod-action mod-action--suspend"
              data-recommended={opusAction === 'suspend_user' ? 'true' : undefined}
              disabled={busy}
              onClick={() =>
                onAct(
                  event.id,
                  'suspend_user',
                  'Suspend this member?\n\nThey will not be able to sign in or be matched until reinstated. Active conversations end immediately.',
                )
              }
            >
              <span className="mod-action-verb">Suspend</span>
              <span className="mod-action-hint">Lock them out, end active chats. Reversible.</span>
              {opusAction === 'suspend_user' && <span className="mod-action-rec">★ Opus pick</span>}
            </button>
            <button
              className="mod-action mod-action--ban"
              disabled={busy}
              onClick={() =>
                onAct(
                  event.id,
                  'ban_user',
                  'Ban this member?\n\nPermanent removal. All active conversations end. They cannot be matched again. This is the most severe action — use for repeat offenders or sexual / harassment violations with clear intent.',
                )
              }
            >
              <span className="mod-action-verb">Ban</span>
              <span className="mod-action-hint">
                Permanent removal. End every conversation. Use sparingly.
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** The moderator review queue — AI flags and user reports awaiting a decision. */
export function ReviewQueue({ me }: { me: MeResponse }) {
  const communityId = me.memberships[0]?.communityId ?? '';
  const [data, setData] = useState<QueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!communityId) return;
    fetchQueue(communityId)
      .then(setData)
      .catch(() => setError('Could not load the review queue.'));
  }, [communityId]);

  useEffect(load, [load]);

  async function act(eventId: string, action: ModeratorAction, confirmMessage?: string) {
    if (confirmMessage) {
      // confirmMessage from the buttons is "Title\n\nDetails" — split
      // back into title + body for the styled dialog. The ban / suspend
      // hits ship with that shape (see EventCard onClick handlers).
      const [title, ...rest] = confirmMessage.split('\n\n');
      const message = rest.join('\n\n');
      const isBan = action === 'ban_user';
      const ok = await confirmDialog({
        title: title ?? 'Are you sure?',
        message: message || undefined,
        confirmLabel: isBan ? 'Ban this member' : 'Suspend',
        danger: true,
      });
      if (!ok) return;
    }
    setBusyId(eventId);
    setError(null);
    const result = await resolveEvent(communityId, eventId, action);
    setBusyId(null);
    if (result.ok) load();
    else setError(result.error);
  }

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <h1>Review queue</h1>
        {data?.crisisEscalation && (
          <p className="muted">
            Crisis escalation endpoint: <strong>{data.crisisEscalation.name}</strong> —{' '}
            {data.crisisEscalation.contact}
          </p>
        )}
        {error && <p className="error">{error}</p>}
        {!data && <p>Loading…</p>}
        {data && data.events.length === 0 && <p className="muted">The queue is clear.</p>}
        {data?.events.map((event) => (
          <EventCard
            key={event.id}
            event={event}
            crisisEndpoint={data.crisisEscalation}
            busy={busyId === event.id}
            onAct={act}
          />
        ))}
      </div>
    </main>
  );
}
